/**
 * MachinesTab — redesign v2
 * Machine ID · Name · Owned By · Status · Last Seen · Actions
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMachines } from '../../API/adminAPI';

const Icon = ({ d, size = 15, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const EditIcon    = 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z';
const PlusIcon    = 'M12 5v14 M5 12h14';
const SearchIcon  = 'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z';
const RefreshIcon = 'M1 4v6h6 M23 20v-6h-6 M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15';

const MachinesTab = () => {
  const navigate  = useNavigate();
  const role      = localStorage.getItem('role') || 'user';
  const canCreate = role === 'atoms_admin';

  const [machines, setMachines] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [search,   setSearch]   = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const data = await getMachines();
      setMachines(data.machines || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load machines');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = machines.filter(m =>
    m.machine_id?.toLowerCase().includes(search.toLowerCase()) ||
    m.machine_name?.toLowerCase().includes(search.toLowerCase())
  );

  const openEdit   = m => navigate(`/app/machines/${m.id}/edit`, { state: { machine: m } });
  const openCreate = () => navigate('/app/machines/new');

  return (
    <div>
      {error && <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-300)', pointerEvents: 'none' }}>
            <Icon d={SearchIcon} />
          </div>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search machines…"
            style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-700)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', background: 'none', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', cursor: 'pointer', color: 'var(--ink-500)' }}>
            <Icon d={RefreshIcon} size={14} />
          </button>
          {canCreate && (
            <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--accent)', border: 'none', borderRadius: 'var(--r-md)', cursor: 'pointer', color: '#fff', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500 }}>
              <Icon d={PlusIcon} size={14} color="#fff" />
              New Machine
            </button>
          )}
        </div>
      </div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)', marginBottom: 10 }}>{filtered.length} machine{filtered.length !== 1 ? 's' : ''}</div>

      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px 80px', gap: 0, borderBottom: '2px solid var(--border-strong)', padding: '10px 16px' }}>
            {['Machine ID', 'Name', 'Organisation', 'Channels', 'Actions'].map((h, i) => (
              <div key={i} style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)' }}>{h}</div>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>No machines found</div>
          ) : filtered.map(m => (
            <div
              key={m.id}
              onClick={() => openEdit(m)}
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px 80px', gap: 0, padding: '14px 16px', borderBottom: '1px solid var(--border-hair)', cursor: 'pointer', alignItems: 'center', transition: 'background var(--t-fast)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)', background: 'var(--accent-soft)', display: 'inline-block', padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(8,151,190,0.2)' }}>{m.machine_id}</div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)' }}>{m.machine_name || '—'}</div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)' }}>{m.owned_by || '—'}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-500)', background: 'var(--bg-raised)', display: 'inline-block', padding: '2px 8px', borderRadius: 4 }}>{m.channel_count ?? '—'} ch</div>
              <div onClick={e => e.stopPropagation()}>
                <button title="Edit" onClick={() => openEdit(m)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 4, borderRadius: 'var(--r-sm)' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-500)'}>
                  <Icon d={EditIcon} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MachinesTab;
